// The "Bill To" card on the Quotation form.
//
// Extracted from QuotationFormPage so the resolved state is reachable on its
// own: the panel owns the contact lookup, so a test can render it with a
// contact already chosen instead of having to simulate a click.
//
// READ-ONLY BY DESIGN. The address and GSTIN come from the CRM Contact and are
// snapshotted onto the document by the server. To change them you edit the
// Contact — that is what stops the address on a document and the address in the
// CRM from drifting apart.

import { useState } from 'react';
import { Building2, Search, AlertTriangle, UserPlus, Edit2, Plus, Mail, Phone as PhoneIcon, MapPin } from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import { Button } from '@/shared/components/ui/Button';
import { ContactFormModal } from '@/features/contacts/components/ContactFormModal';
import { useContacts, useContact, useContactMutations } from '@/features/contacts/hooks/useContacts';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useBillToPreview } from '../hooks/useQuotationForm';
import type { CreateContactPayload, UpdateContactPayload, UpdateContactExtendedPayload } from '@/features/contacts/types';

export interface BillToPanelProps {
  contactId: string;
  onSelect: (contactId: string) => void;
  onClear: () => void;
  /** Shown only when the form was opened from a pipeline record. */
  onCreateFromRecord?: () => void;
  creatingContact?: boolean;
  acknowledgedSupply: boolean;
  onAcknowledgeSupply: (next: boolean) => void;
  /**
   * Locks the client to the one already on the document. Used when EDITING:
   * the bill-to block, the GST treatment and the whole document were derived
   * from this account, so swapping it underneath an existing quotation would
   * quietly re-bill it to somebody else. Quote a different client by creating a
   * new quotation.
   */
  locked?: boolean;
  documentLabel?: string;
}

export function BillToPanel({
  contactId, onSelect, onClear, onCreateFromRecord, creatingContact = false,
  acknowledgedSupply, onAcknowledgeSupply, locked = false, documentLabel = 'Quotation',
}: BillToPanelProps) {
  const [search, setSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  // Debounced and lightweight — see ContactSelectField for why.
  const debouncedSearch = useDebounce(search.trim(), 300);
  const queryClient = useQueryClient();
  const { data: contactsData, isLoading: contactsLoading } = useContacts({ search: debouncedSearch, compact: true, totalOnly: true, limit: 30 });
  const contacts = contactsData?.contacts ?? [];
  const { data: preview, isLoading, isError } = useBillToPreview(contactId);
  const { data: fullContact } = useContact(contactId);
  const { createContact, updateContact, updateContactExtended } = useContactMutations();

  // Full contact modal (handles both Create and Edit with all details)
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalMode, setContactModalMode] = useState<'create' | 'edit'>('create');
  const [savingContact, setSavingContact] = useState(false);

  const handleOpenCreateContact = () => {
    setContactModalMode('create');
    setContactModalOpen(true);
  };

  const handleOpenEditContact = () => {
    setContactModalMode('edit');
    setContactModalOpen(true);
  };

  const handleContactSubmit = async (
    basic: CreateContactPayload | UpdateContactPayload,
    extended: UpdateContactExtendedPayload
  ) => {
    try {
      setSavingContact(true);
      if (contactModalMode === 'create') {
        const res = await createContact.mutateAsync(basic as CreateContactPayload);
        const newId = res.id || (res as any)._id;
        if (newId && Object.keys(extended).length > 0) {
          await updateContactExtended.mutateAsync({ id: newId, payload: extended });
        }
        await queryClient.invalidateQueries({ queryKey: ['contacts'] });
        if (newId) {
          onSelect(newId);
          await queryClient.invalidateQueries({ queryKey: ['quotations', 'bill-to-preview', newId] });
          await queryClient.invalidateQueries({ queryKey: ['sales', 'bill-to', newId] });
        }
        toast.success('Contact created and selected for quotation.');
      } else if (contactId) {
        await updateContact.mutateAsync({ id: contactId, payload: basic as UpdateContactPayload });
        if (Object.keys(extended).length > 0) {
          await updateContactExtended.mutateAsync({ id: contactId, payload: extended });
        }
        await queryClient.invalidateQueries({ queryKey: ['contacts'] });
        await queryClient.invalidateQueries({ queryKey: ['contacts', 'detail', contactId] });
        await queryClient.invalidateQueries({ queryKey: ['quotations', 'bill-to-preview', contactId] });
        await queryClient.invalidateQueries({ queryKey: ['sales', 'bill-to', contactId] });
        toast.success('Contact details and billing address updated.');
      }
      setContactModalOpen(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to save contact.');
    } finally {
      setSavingContact(false);
    }
  };

  // EVERY read below goes through these, never through `preview?.a.b`.
  // Optional chaining short-circuits on the FIRST link only, so
  // `preview?.billTo.billingAddress` still throws when the payload arrives
  // without a billTo — which is exactly what a 404 body, an older backend or a
  // proxy error page produces. That was the crash this panel was split out of.
  const billTo = preview?.billTo;
  const address = billTo?.billingAddress;
  const placeOfSupply = preview?.placeOfSupply;
  const addressLines = [
    address?.street,
    [address?.city, address?.zipCode].filter(Boolean).join(' '),
    address?.state,
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-outline bg-surface p-5 shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-outline/60 pb-3">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-primary" />
          <h2 className="text-body-base font-semibold text-on-surface">Bill To</h2>
        </div>
        {contactId && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleOpenEditContact}
              className="inline-flex items-center gap-1 text-body-xs font-semibold text-primary hover:underline"
            >
              <Edit2 className="h-3.5 w-3.5" />
              Edit Contact (All Details)
            </button>
            {!locked && (
              <button
                type="button"
                onClick={() => { onClear(); setPickerOpen(true); }}
                className="text-body-xs font-semibold text-on-surface-variant hover:underline"
              >
                Change
              </button>
            )}
          </div>
        )}
        {locked && (
          <span className="text-body-xs font-medium text-on-surface-variant">Fixed for this {documentLabel.toLowerCase()}</span>
        )}
      </div>

      {!contactId ? (
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPickerOpen(true); }}
              onFocus={() => setPickerOpen(true)}
              placeholder="Search contacts by name or company…"
              className="w-full rounded-xl border border-outline bg-surface-container-low py-2 pl-9 pr-3 text-body-sm text-on-surface placeholder:text-on-surface-variant/70 focus:border-primary focus:outline-none"
            />
          </div>

          {pickerOpen && (
            <div className="max-h-64 overflow-y-auto rounded-xl border border-outline">
              {contactsLoading ? (
                <div className="p-4 text-center"><Spinner /></div>
              ) : contacts.length === 0 ? (
                <p className="p-4 text-center text-body-xs text-on-surface-variant">
                  {search ? `No contacts match “${search}”.` : 'No contacts yet.'}
                </p>
              ) : (
                contacts.slice(0, 30).map((c, i) => (
                  <button
                    // A contact with no id cannot be selected, but it must not
                    // break the list either — hence the index fallback key.
                    key={c.id || `row-${i}`}
                    type="button"
                    disabled={!c.id}
                    onClick={() => { if (c.id) { onSelect(c.id); setPickerOpen(false); } }}
                    className="flex w-full flex-col items-start gap-0.5 border-b border-outline/60 px-3 py-2 text-left transition last:border-0 hover:bg-surface-container-low disabled:opacity-50"
                  >
                    <span className="text-body-sm font-semibold text-on-surface">{c.company || c.name}</span>
                    <span className="text-body-xs text-on-surface-variant">
                      {[c.company ? c.name : null, c.creator_name ? `Owner: ${c.creator_name}` : null, c.phone, c.email].filter(Boolean).join(' · ') || 'No contact details'}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleOpenCreateContact}
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary/10 border border-primary/20 px-3 py-2 text-body-xs font-semibold text-primary transition hover:bg-primary/20"
            >
              <Plus className="h-3.5 w-3.5" />
              Create New Contact
            </button>
            {onCreateFromRecord && (
              <button
                type="button"
                onClick={onCreateFromRecord}
                disabled={creatingContact}
                className="inline-flex items-center gap-1.5 rounded-xl border border-dashed border-outline px-3 py-2 text-body-xs font-semibold text-on-surface transition hover:bg-surface-container-low disabled:opacity-60"
              >
                <UserPlus className="h-3.5 w-3.5" />
                {creatingContact ? 'Creating…' : 'Create a contact from this record'}
              </button>
            )}
          </div>
          <p className="text-body-xs text-on-surface-variant">
            Search first — creating a second profile for a client you already have is how duplicate
            accounts appear.
          </p>
        </div>
      ) : isLoading ? (
        <div className="p-4"><Spinner /></div>
      ) : isError || !billTo ? (
        // The lookup failed. Say so plainly instead of rendering an empty card
        // that looks like a client with no details on file.
        <div className="flex flex-col gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3.5 text-body-xs text-rose-900">
          <p className="flex items-center gap-1.5 font-bold">
            <AlertTriangle className="h-3.5 w-3.5" /> Could not load this client&apos;s billing details
          </p>
          <p>The contact may have been deleted, or the server may need a restart.</p>
          {!locked && (
            <button type="button" onClick={onClear} className="w-fit font-bold underline">
              Choose a different client
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="rounded-xl bg-surface-container-low p-3.5">
            <p className="text-body-base font-bold text-on-surface">{billTo.customerName || '—'}</p>
            <p className="mt-1 whitespace-pre-line text-body-sm text-on-surface-variant">
              {addressLines.length > 0 ? addressLines.join('\n') : 'No address on file'}
            </p>
            <p className="mt-2 text-body-xs font-semibold text-on-surface">
              GSTIN: <span className="font-mono">{billTo.customerGstin || '—'}</span>
            </p>
            {/* Mobile and email come from the same contact and print on the
                quotation — shown so a wrong number is caught here. */}
            {(billTo.customerPhone || billTo.customerEmail) && (
              <p className="mt-1 flex flex-wrap gap-x-3 text-body-xs text-on-surface-variant">
                {billTo.customerPhone && <span className="inline-flex items-center gap-1"><PhoneIcon className="h-3 w-3" aria-hidden /> {billTo.customerPhone}</span>}
                {billTo.customerEmail && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" aria-hidden /> {billTo.customerEmail}</span>}
              </p>
            )}
          </div>

          {(preview?.missingAddress || preview?.missingGstin) && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-body-xs text-amber-900">
              <p className="flex items-center gap-1.5 font-bold">
                <AlertTriangle className="h-3.5 w-3.5" />
                {preview?.missingAddress && preview?.missingGstin
                  ? 'Address and GSTIN are missing'
                  : preview?.missingAddress ? 'Address is missing' : 'GSTIN is missing'}
              </p>
              <p className="mt-1">
                They are read from the contact record, so add them there and reload. You can still save
                this as a draft.
              </p>
              <button
                type="button"
                onClick={handleOpenEditContact}
                className="mt-1.5 inline-flex items-center gap-1 font-bold text-amber-900 underline text-left"
              >
                <Edit2 className="h-3 w-3" /> Edit Contact (All Details)
              </button>
            </div>
          )}

          <div className="rounded-xl border border-outline/70 p-3">
            <p className="text-body-xs font-bold uppercase tracking-wider text-on-surface-variant">
              Place of Supply
            </p>
            <p className="mt-0.5 text-body-sm font-semibold text-on-surface">
              {placeOfSupply?.state || 'Not determined'}
              <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-body-xs font-bold text-primary">
                {preview?.isExport ? 'No GST (export)' : preview?.intraState ? 'CGST + SGST' : 'IGST'}
              </span>
            </p>
            {preview?.assumedPlaceOfSupply && (
              <label className="mt-2 flex cursor-pointer items-start gap-2 rounded-lg bg-amber-50 p-2.5 text-body-xs text-amber-900">
                <input
                  type="checkbox"
                  checked={acknowledgedSupply}
                  onChange={(e) => onAcknowledgeSupply(e.target.checked)}
                  className="mt-0.5 rounded accent-amber-600"
                />
                <span>
                  This client has no state or GSTIN recorded, so the supply is assumed to be within
                  Maharashtra and taxed as CGST + SGST. Tick to confirm before issuing.
                </span>
              </label>
            )}
          </div>
        </div>
      )}

      <ContactFormModal
        open={contactModalOpen}
        contact={contactModalMode === 'edit' ? (fullContact as any) : null}
        loading={savingContact}
        onClose={() => setContactModalOpen(false)}
        onSubmit={handleContactSubmit}
      />
    </div>
  );
}

export default BillToPanel;
